import React, { useMemo } from 'react';

export default function AbsenceTableView({ workers, absences, getStatus, onEntryClick, sortConfig, onSort }) {
    
    // Helper to format date/time nicely
    const formatDateTime = (dateStr, allDay) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        
        const isToday = new Date().toDateString() === d.toDateString();
        
        const datePart = isToday ? 'Heute' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        if (allDay) return datePart;
        
        const timePart = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        return `${datePart} ${timePart}`;
    };

    const formatAbsenceRange = (abs) => {
        if (!abs) return '—';
        const start = formatDateTime(abs.start_datetime, abs.all_day);
        const end = formatDateTime(abs.end_datetime, abs.all_day);
        if (abs.all_day && start === end) return start; // Single day
        return `${start} - ${end}`;
    };

    const getNextAbsence = (workerId) => {
        const absList = absences[workerId] || [];
        const now = new Date();
        const futureAbsences = absList.filter(a => new Date(a.start_datetime) > now);
        if (futureAbsences.length === 0) return null;
        
        futureAbsences.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        return futureAbsences[0];
    };

    const processedWorkers = useMemo(() => {
        let items = [...workers];
        
        items.sort((a, b) => {
            let aVal, bVal;
            
            const aStatus = getStatus(a.id);
            const bStatus = getStatus(b.id);
            const aNext = getNextAbsence(a.id);
            const bNext = getNextAbsence(b.id);

            switch(sortConfig.key) {
                case 'name': 
                    aVal = a.name || ''; 
                    bVal = b.name || ''; 
                    break;
                case 'status': 
                    aVal = aStatus.label; 
                    bVal = bStatus.label; 
                    break;
                case 'current_absence':
                    aVal = aStatus.absence ? new Date(aStatus.absence.end_datetime).getTime() : 0;
                    bVal = bStatus.absence ? new Date(bStatus.absence.end_datetime).getTime() : 0;
                    break;
                case 'next_absence':
                    aVal = aNext ? new Date(aNext.start_datetime).getTime() : Number.MAX_SAFE_INTEGER;
                    bVal = bNext ? new Date(bNext.start_datetime).getTime() : Number.MAX_SAFE_INTEGER;
                    break;
                default: 
                    aVal = a.name || ''; 
                    bVal = b.name || '';
            }

            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return items;
    }, [workers, getStatus, absences, sortConfig]);

    const formatAbsenceType = (type) => {
        const types = { 
            'present': 'Anwesend',
            'urlaub': 'Urlaub', 
            'krank': 'Krankheit', 
            'termin': 'Termin', 
            'sonstiges': 'Sonstiges',
            'abwesend': 'Sonstiges',
            'dienstreise': 'Termin'
        };
        const label = types[String(type).toLowerCase()] || 'Sonstiges';
        return label;
    };

    const getAvatarColor = (name) => {
        const colors = ['#007aff', '#5856d6', '#af52de', '#ff2d55', '#ff9500', '#34c759', '#5ac8fa', '#ff6482', '#30b0c7'];
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return colors[Math.abs(hash) % colors.length];
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <div className="employee-table-container">
            <table className="employee-table">
                <thead>
                    <tr>
                        <th onClick={() => onSort('name')} className={`sort-header col-name ${sortConfig.key === 'name' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Mitarbeiter</span>
                                <span className="sort-indicator">{sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('status')} className={`sort-header col-center ${sortConfig.key === 'status' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Aktueller Status</span>
                                <span className="sort-indicator">{sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('current_absence')} className={`sort-header col-center ${sortConfig.key === 'current_absence' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Details (Aktuell)</span>
                                <span className="sort-indicator">{sortConfig.key === 'current_absence' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('next_absence')} className={`sort-header col-center ${sortConfig.key === 'next_absence' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Nächste Abwesenheit</span>
                                <span className="sort-indicator">{sortConfig.key === 'next_absence' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th style={{ textAlign: 'center' }}>Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {processedWorkers.map(emp => {
                        const status = getStatus(emp.id);
                        const currentAbs = status.absence;
                        const nextAbs = getNextAbsence(emp.id);

                        return (
                            <tr key={emp.id}>
                                <td className="col-name" onClick={() => onEntryClick(emp.id, null)} style={{ cursor: 'pointer' }}>
                                    <div className="employee-table-name-cell">
                                        <div className="employee-table-avatar" style={{ background: emp.color || getAvatarColor(emp.name) }}>
                                            {getInitials(emp.name)}
                                        </div>
                                        <div>
                                            <div className="employee-table-name">{emp.name}</div>
                                            <div className="employee-table-role" style={{ fontSize: '11px', color: '#86868b' }}>{emp.category_name || '—'}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="col-center">
                                    <div 
                                        className={`employee-table-badge clickable ${status.badge}`}
                                        onClick={() => onEntryClick(emp.id, currentAbs ? currentAbs.id : null)}
                                    >
                                        <span className={`status-dot-mini`} style={{ background: status.color, marginRight: '6px', display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%' }}></span>
                                        {formatAbsenceType(status.type)}
                                    </div>
                                </td>
                                <td className="col-center">
                                    {currentAbs ? (
                                        <div 
                                            className="absence-clickable-cell"
                                            onClick={() => onEntryClick(emp.id, currentAbs.id)}
                                            title="Abwesenheit bearbeiten"
                                        >
                                            <span style={{ fontWeight: 600 }}>Bis:</span> {formatDateTime(currentAbs.end_datetime, currentAbs.all_day)}
                                        </div>
                                    ) : (
                                        <span style={{ color: '#86868b' }}>—</span>
                                    )}
                                </td>
                                <td className="col-center">
                                    {nextAbs ? (
                                        <div 
                                            className="absence-clickable-cell"
                                            onClick={() => onEntryClick(emp.id, nextAbs.id)}
                                            title="Abwesenheit bearbeiten"
                                        >
                                            <div style={{ fontWeight: 600 }}>{formatAbsenceType(nextAbs.absence_type)}</div>
                                            <div style={{ fontSize: '11px' }}>{formatAbsenceRange(nextAbs)}</div>
                                        </div>
                                    ) : (
                                        <span style={{ color: '#86868b' }}>Keine geplant</span>
                                    )}
                                </td>
                                <td>
                                    <div className="employee-table-actions" style={{ justifyContent: 'center' }}>
                                        <button className="employee-table-action-btn" onClick={() => onEntryClick(emp.id, null)} title="Abwesenheiten verwalten">
                                            📝
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
